import {Component, OnDestroy, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {DataTableDirective} from 'angular-datatables';
import {escapeHtml, ranaDtOptions, RanaDtOptions} from '../shared/rana-datatable-defaults';
import {ActivatedRoute, Router} from "@angular/router";
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ChapterApiService} from '../api/chapter-api.service';
import {ToastrService} from 'ngx-toastr';
import {CommitteeDTO, CommitteeService} from '../api/committee-api.service';
import {UserApiService} from '../api/user-api.service';
import {SharedService} from '../api/shared-service';
import {SpinnerService} from '../shared/services/spinner.service';
import {finalize} from 'rxjs';
import {
    ChapterCommunicationHistoryItem,
    ChapterCommunicationRequest,
    ChapterCommunicationResponse,
    CommunicationScope,
    EmailApiService
} from '../api/email-api.service';
import {MembershipType} from '../constants';
import {NotificationApiService} from '../api/notification-api.service';
import {ChapterNotification} from '../models/notification.model';
import {EventDTO, EventService} from '../api/event-api.service';
import {RoleScope, reachesAllChapters, reachesChapter, storedRoleScope} from '../shared/access';

/** Chapter id of the NEC workspace (the national chapter). */
const NEC_CHAPTER_ID = '0';

@Component({
    selector: 'app-chapter-home',

    templateUrl: './chapter-home.component.html',
    styleUrl: './chapter-home.component.css'
})
export class ChapterHomeComponent implements OnDestroy {
    @ViewChild(DataTableDirective) dtElement!: DataTableDirective;
    dtOptions: RanaDtOptions = ranaDtOptions({
        columns: [
            {title: 'Sent Date', data: 'sentDate', render: (d: any) => escapeHtml(d || '--')},
            {title: 'Subject', data: 'subject', render: (d: any) => escapeHtml(d)},
            {title: 'Scope', data: 'scope', render: (d: any, type: any, row: any) => escapeHtml(this.getCommunicationScopeLabel(row.scope))},
            {title: 'Delivered', data: 'deliveredCount'},
            {title: 'Failed', data: 'failedCount'}
        ]
    });
    dtTrigger: Subject<any> = new Subject();

    showWelcomeNote = true;
    showCommittee = true;
    chapterId!: string;
    memberForm: FormGroup;
    chapter: any;
    chapterForm: FormGroup;
    chapterCreationError = false;
    uploadedImageUrl: string | null = null;
    selectedCommittee?: CommitteeDTO;
    selectedMember: any;
    userRole: any;
    userChapter: any;
    userGroup: any;
    roleScope: RoleScope = storedRoleScope();

    /** True for roles whose reach is every chapter (system administrator and the NEC tier), as decided by the API. */
    get allChapters(): boolean {
        return reachesAllChapters(this.roleScope);
    }
    stripeForm!: FormGroup;
    readonly DEFAULT_STRIPE_TARGET = 'DEFAULT';
    chapterEvents: EventDTO[] = [];
    chapterList: any[] = [];
    chapterUsers: any[] = [];
    filteredChapterUsers: any[] = [];
    memberDirectoryLoaded = false;
    memberDirectoryDetailed = false; // true when the rows carry contact details/addresses (executive committee view)
    memberSearchTerm = '';
    selectedDirectoryUserId: string | null = null;
    activeSectionLabel = 'President Note';
    communicationForm: FormGroup;
    communicationResult: ChapterCommunicationResponse | null = null;
    communicationHistory: ChapterCommunicationHistoryItem[] = [];
    sendingCommunication = false;
    loadingCommunicationHistory = false;
    isNecExecutive = false;

    /**
     * Finance & Payments in this workspace's sidebar. NEC roles see it on every chapter (and open that chapter's
     * payments); a chapter leader only on their own chapter. The server enforces the same scope regardless.
     */
    get canSeeFinance(): boolean {
        if (!this.userRole?.FINANCE?.includes('READ')) return false;
        const necTier = !!this.userRole?.FINANCE?.includes('APPROVE');
        return necTier || this.userChapter === this.chapterId;
    }

    /** The NEC workspace opens the all-chapters view; any other chapter opens that chapter's payments. */
    get financeQueryParams(): { [k: string]: string } {
        return this.chapter?.name === 'NEC' || !this.chapterId ? {} : {chapter: String(this.chapterId)};
    }
    // Finance & Payments is a section of this workspace (so the sidebar stays); built the first time it is opened.
    financeOpened = false;
    // Public Document Management likewise opens inside the workspace.
    documentsOpened = false;
    openDocuments(): void {
        this.setActiveSection('Public Document Management');
        this.documentsOpened = true;
    }

    openFinance(): void {
        this.setActiveSection('Finance & Payments');
        this.financeOpened = true;
    }

    isChapterExecutive = false;
    communicationScopeOptions: Array<{ value: CommunicationScope; label: string }> = [];

    // Notification management
    chapterNotifications: ChapterNotification[] = [];
    notificationForm: FormGroup;
    editingNotification: ChapterNotification | null = null;

    constructor(private route: ActivatedRoute,
                private sharedService: SharedService,
                private fb: FormBuilder,
                private toastr: ToastrService,
                private chapterApiService: ChapterApiService,
                private committeeService: CommitteeService,
                private usereApiService: UserApiService,
                private emailApiService: EmailApiService,
                private notificationApiService: NotificationApiService,
                private router: Router,
                private spinner: SpinnerService,
                private eventApiService: EventService
    ) {
        this.memberForm = this.fb.group({
            role: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]]
        });
        this.chapterForm = this.fb.group({

            welcomeNote: ['', [Validators.required]],
        });
        this.stripeForm = this.fb.group({
            stripeTarget: [this.DEFAULT_STRIPE_TARGET, Validators.required],
            stripePublicKey: ['', Validators.required],
            stripeSecretKey: ['', Validators.required]
        });
        this.communicationForm = this.fb.group({
            subject: ['', [Validators.required]],
            body: ['', [Validators.required]],
            scope: ['MY_CHAPTER', [Validators.required]],
            includeNonMembers: [true],
            chapterIds: []
        });
        this.notificationForm = this.fb.group({
            message: ['', [Validators.required]],
            targetURL: [''],
            expireAt: ['', [Validators.required]]
        });

        this.chapterId = this.route.snapshot.paramMap.get('id')!;

        //this.sharedService.userRole$.subscribe((role) => {
        this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        //});

        this.userChapter = sessionStorage.getItem('chapter');
        this.userGroup = sessionStorage.getItem('userGroup') || '{}';
        this.roleScope = storedRoleScope();


    }


    ngOnInit(): void {

        this.sharedService.userRole$.subscribe((role) => {
            //  this.userRole = JSON.parse(role);
            if (typeof role === 'string') {
                this.userRole = JSON.parse(role);
            } else {
                this.userRole = role;
            }
        });


        this.userChapter = sessionStorage.getItem('chapter');
        this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        this.userGroup = sessionStorage.getItem('userGroup') || '{}';
        this.roleScope = storedRoleScope();


        this.chapterId = this.route.snapshot.paramMap.get('id')!;

        this.chapterApiService.getData().subscribe({
            next: (response) => {
                this.chapterList = response || [];
                this.applyCommunicationScopeDefaults();
            },
            error: (error) => {
                console.error('Failed to load chapter list', error);
            }
        });

        // or subscribe for dynamic updates
        this.route.paramMap.subscribe(params => {
            this.chapterId = params.get('id')!;

            // The NEC workspace (chapter "0") is for NEC roles. Anyone else who types its URL lands on their own chapter.
            if (!this.canOpenWorkspace(this.chapterId)) {
                this.redirectToOwnChapter();
                return;
            }

            // For the Stripe Configuration target dropdown (Default + this chapter's events).
            // Closed events take no further payments, so they're excluded here.
            this.eventApiService.getEventsByChapterId(this.chapterId).subscribe({
                next: (events) => this.chapterEvents = (events || []).filter(e => e.status !== 'CLOSED'),
                error: () => this.chapterEvents = []
            });

            this.spinner.show(); // Show spinner while loading chapter data
            this.chapterApiService.getChapterData(this.chapterId).pipe(
                finalize(() => this.spinner.hide()) // ✅ Hide spinner after outermost call completes
            ).subscribe(
                (response) => {

                    this.chapter = response;
                    this.applyCommunicationScopeDefaults();
                    this.memberDirectoryLoaded = false;
                    this.selectedDirectoryUserId = null;
                    this.chapterUsers = [];
                    this.filteredChapterUsers = [];
                    this.memberSearchTerm = '';
                },
                (error) => {
                    // Handle login error (invalid credentials, server error, etc.)
                    console.error('Failed to load chapters', error);

                }
            );
        });

    }


    editWelcomeNote() {
        this.showWelcomeNote = !this.showWelcomeNote;
        this.activeSectionLabel = 'President Note';
    }

    setActiveSection(label: string): void {
        this.activeSectionLabel = label;
    }

    get chapterRootLink(): string[] {
        return ['/chapter-home', this.userChapter || this.chapterId];
    }

    /** Same rule the header uses to show the NEC link: only roles that can create chapters (the NEC tier). */
    private canOpenWorkspace(chapterId: string): boolean {
        if (chapterId !== NEC_CHAPTER_ID) return true;
        return this.allChapters;
    }

    private redirectToOwnChapter(): void {
        const own = this.userChapter && this.userChapter !== NEC_CHAPTER_ID ? this.userChapter : null;
        this.toastr.warning('The NEC workspace is only available to NEC members.');
        if (own) {
            this.router.navigate(['/chapter-home', own]);
        } else {
            this.router.navigate(['/profile']);
        }
    }

    openMemberDirectory(): void {
        this.setActiveSection('Member Directory');
        if (!this.memberDirectoryLoaded) {
            this.loadChapterUsers();
        }
    }

    openMemberLookup(user?: any): void {
        this.setActiveSection('Search Users');
        if (user?.id) {
            this.selectedDirectoryUserId = user.id;
        }
        if (!this.memberDirectoryLoaded) {
            this.loadChapterUsers();
        }
    }

    loadChapterUsers(force = false): void {
        if (this.memberDirectoryLoaded && !force) {
            return;
        }

        const detailed = this.canViewMemberContactDetails;
        const request = detailed
            ? this.usereApiService.getChapterDirectory(this.chapterId)
            : this.usereApiService.getUsersByChapterId(this.chapterId);

        this.spinner.show();
        request.pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (response) => {
                const users = response || [];
                this.memberDirectoryDetailed = detailed;
                this.chapterUsers = [...users].sort((a: any, b: any) =>
                    `${a.firstname || ''} ${a.lastname || ''}`.localeCompare(`${b.firstname || ''} ${b.lastname || ''}`)
                );
                this.filteredChapterUsers = [...this.chapterUsers];
                this.memberDirectoryLoaded = true;
            },
            error: (err) => {
                console.error('Failed to load chapter members', err);
                this.toastr.error('Failed to load chapter member directory');
            }
        });
    }

    applyMemberFilter(): void {
        const q = (this.memberSearchTerm || '').trim().toLowerCase();
        if (!q) {
            this.filteredChapterUsers = [...this.chapterUsers];
            return;
        }
        this.filteredChapterUsers = this.chapterUsers.filter((user) => (
            `${user.firstname || ''} ${user.lastname || ''} ${user.email || ''} ${user.phone || ''} ${this.formatMemberAddress(user)}`
                .toLowerCase().includes(q)
        ));
    }

    // Mirrors the API rule (ChapterDirectoryAccess): NEC-level roles see every chapter,
    // a chapter's president/CEC see only their own. The API enforces it; this only picks the view.
    get canViewMemberContactDetails(): boolean {
        return reachesChapter(this.roleScope, this.userChapter, this.chapterId);
    }

    formatMemberAddress(user: any): string {
        const street = [user?.addressLine1, user?.addressLine2].filter(Boolean).join(', ');
        const stateZip = [user?.state, user?.zipCode].filter(Boolean).join(' ');
        return [street, user?.city, stateZip].filter(Boolean).join(', ');
    }

    exportMemberDirectoryCsv(): void {
        if (!this.memberDirectoryDetailed) {
            return;
        }
        const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Address Line 1', 'Address Line 2', 'City', 'State', 'Zip Code', 'Country', 'Membership'];
        const rows = this.filteredChapterUsers.map(user => [
            user.firstname || '', user.lastname || '', user.email || '', user.phone || '',
            user.addressLine1 || '', user.addressLine2 || '', user.city || '', user.state || '',
            user.zipCode || '', user.country || '', this.getMembershipLabel(user.memberType)
        ]);
        const chapterName = (this.chapter?.name || `chapter-${this.chapterId}`).replace(/[^A-Za-z0-9_-]+/g, '-');
        this.downloadCsv(`${chapterName}-member-directory.csv`, headers, rows);
    }

    private downloadCsv(filename: string, headers: string[], rows: (string | number)[][]): void {
        const escapeCell = (value: string | number): string => {
            let str = String(value ?? '');
            // Member-entered text: stop spreadsheets from treating it as a formula (CSV injection).
            if (/^[=+\-@]/.test(str)) {
                str = `'${str}`;
            }
            return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
        };
        const lines = [headers, ...rows].map(row => row.map(escapeCell).join(','));
        const csvContent = lines.join('\r\n');
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }

    memberInitials(user: any): string {
        const first = (user?.firstname || '').charAt(0);
        const last = (user?.lastname || '').charAt(0);
        return (first + last).toUpperCase() || '?';
    }

    isPaidMember(user: any): boolean {
        const memberType = (user?.memberType || '').toUpperCase();
        return [MembershipType.MEMBER, MembershipType.LIFE_MEMBER, MembershipType.ANNUAL_MEMBER].includes(memberType as MembershipType);
    }

    getMembershipLabel(memberType: string): string {
        const type = (memberType || '').toUpperCase();
        if (type === MembershipType.NON_MEMBER) {
            return 'Non Member';
        }
        if (type === MembershipType.LIFE_MEMBER) {
            return 'Life Member';
        }
        if (type === MembershipType.ANNUAL_MEMBER) {
            return 'Annual Member';
        }
        return 'Member';
    }

    onCommunicationScopeChange(): void {
        const scope = this.communicationForm.get('scope')?.value as CommunicationScope;
        if (scope !== 'SPECIFIC_CHAPTERS') {
            this.communicationForm.patchValue({chapterIds: []});
        }
        this.communicationResult = null;
    }

    sendChapterCommunication(): void {
        if (!this.isChapterExecutive) {
            this.toastr.error('You are not authorized to send chapter communication.');
            return;
        }

        if (this.communicationForm.invalid) {
            this.communicationForm.markAllAsTouched();
            this.toastr.error('Please complete communication subject and message.');
            return;
        }

        const scope = this.communicationForm.get('scope')?.value as CommunicationScope;
        const chapterControlValue = this.communicationForm.get('chapterIds')?.value;
        const chapterIds: string[] = (Array.isArray(chapterControlValue) ? chapterControlValue : [chapterControlValue])
            .filter(chapterId => !!chapterId);

        if (this.isNecExecutive && scope === 'SPECIFIC_CHAPTERS' && chapterIds.length === 0) {
            this.toastr.error('Select at least one chapter for chapter-specific communication.');
            return;
        }

        const payload: ChapterCommunicationRequest = {
            subject: this.communicationForm.get('subject')?.value,
            body: this.communicationForm.get('body')?.value,
            scope: this.isNecExecutive ? scope : 'MY_CHAPTER',
            includeNonMembers: !!this.communicationForm.get('includeNonMembers')?.value,
            chapterIds: this.isNecExecutive ? chapterIds : [this.chapterId]
        };

        this.sendingCommunication = true;
        this.emailApiService.sendChapterCommunication(payload).pipe(
            finalize(() => this.sendingCommunication = false)
        ).subscribe({
            next: (response) => {
                this.communicationResult = response;
                this.loadCommunicationHistory();
                if (response.failedCount > 0) {
                    this.toastr.warning(`Delivered: ${response.deliveredCount}, Failed: ${response.failedCount}`);
                } else {
                    this.toastr.success(`Communication sent. Delivered: ${response.deliveredCount}`);
                }
            },
            error: (err) => {
                const message = err?.error?.message || 'Failed to send chapter communication';
                this.toastr.error(message);
            }
        });
    }

    private applyCommunicationScopeDefaults(): void {
        const groups = (this.userGroup || '')
            .split(',')
            .map((group: string) => group.trim())
            .filter((group: string) => !!group);
        const hasNecRole = this.allChapters;
        const hasChapterExecRole = this.roleScope === 'OWN_CHAPTER';

        this.isNecExecutive = hasNecRole && this.userChapter === this.chapterId;
        this.isChapterExecutive = this.isNecExecutive || (hasChapterExecRole && this.userChapter === this.chapterId);

        if (this.isNecExecutive) {
            this.communicationScopeOptions = [
                {value: 'ALL_USERS', label: 'All Chapters'},
                {value: 'SPECIFIC_CHAPTERS', label: 'Specific Chapters'}
            ];
            if (!['ALL_USERS', 'SPECIFIC_CHAPTERS'].includes(this.communicationForm.get('scope')?.value)) {
                this.communicationForm.patchValue({scope: 'ALL_USERS'});
            }
        } else if (this.isChapterExecutive) {
            this.communicationScopeOptions = [
                {value: 'MY_CHAPTER', label: 'My Chapter'}
            ];
            this.communicationForm.patchValue({scope: 'MY_CHAPTER', chapterIds: []});
        } else {
            this.communicationScopeOptions = [];
        }
        if (this.isChapterExecutive) {
            this.loadCommunicationHistory();
        } else {
            this.communicationHistory = [];
            this.refreshCommunicationTable();
        }
    }

    openCommunicationSection(): void {
        this.setActiveSection('Communication');
        if (this.isChapterExecutive) {
            this.loadCommunicationHistory();
        }
    }

    getCommunicationScopeLabel(scope?: CommunicationScope): string {
        if (scope === 'ALL_USERS') {
            return 'All Chapters';
        }
        if (scope === 'SPECIFIC_CHAPTERS') {
            return 'Specific Chapters';
        }
        return 'My Chapter';
    }

    private loadCommunicationHistory(): void {
        if (!this.chapterId || !this.isChapterExecutive) {
            return;
        }
        this.loadingCommunicationHistory = true;
        this.emailApiService.getChapterCommunicationHistory(this.chapterId).pipe(
            finalize(() => this.loadingCommunicationHistory = false)
        ).subscribe({
            next: (history) => {
                this.communicationHistory = history || [];
                this.refreshCommunicationTable();
            },
            error: (error) => {
                console.error('Failed to load chapter communication history', error);
                this.communicationHistory = [];
                this.refreshCommunicationTable();
                this.toastr.error('Failed to load communication history');
            }
        });
    }

    private refreshCommunicationTable(): void {
        this.dtOptions.data = this.communicationHistory;
        if (this.dtElement?.dtInstance) {
            this.dtElement.dtInstance.then(dt => {
                dt.destroy();
                this.dtTrigger.next(null);
            });
        } else {
            this.dtTrigger.next(null);
        }
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    get selectedStripeEvent(): EventDTO | undefined {
        const target = this.stripeForm.get('stripeTarget')?.value;
        return this.chapterEvents.find(e => e.id === target);
    }

    // Never carry a key typed for one target over to another when the dropdown changes.
    onStripeTargetChange(): void {
        this.stripeForm.patchValue({ stripePublicKey: '', stripeSecretKey: '' });
    }

    updateStripeKeys(): void {
        if (this.stripeForm.invalid) {
            this.toastr.error('Please enter both Stripe keys', 'Validation Error');
            return;
        }

        const { stripeTarget, stripePublicKey, stripeSecretKey } = this.stripeForm.value;
        const isDefault = stripeTarget === this.DEFAULT_STRIPE_TARGET;

        this.spinner.show();
        const request$ = isDefault
            ? this.chapterApiService.updateStripeKeys(this.chapterId, stripePublicKey, stripeSecretKey)
            : this.eventApiService.updateEventStripeKeys(stripeTarget, stripePublicKey, stripeSecretKey);

        request$.pipe(
            finalize(() => this.spinner.hide())
        ).subscribe({
            next: (updated) => {
                this.toastr.success('Stripe keys updated successfully', 'Success');
                this.stripeForm.patchValue({ stripePublicKey: '', stripeSecretKey: '' });
                if (isDefault) {
                    this.chapter = { ...this.chapter, stripeEnabled: updated?.stripeEnabled };
                } else {
                    const idx = this.chapterEvents.findIndex(e => e.id === stripeTarget);
                    if (idx > -1) this.chapterEvents[idx] = updated as EventDTO;
                }
            },
            error: () => {
                this.toastr.error('Failed to update Stripe keys', 'Error');
            }
        });
    }

    // -------------------- Notification Management --------------------

    openNotificationSection(): void {
        this.setActiveSection('Notifications');
        this.loadChapterNotifications();
    }

    loadChapterNotifications(): void {
        this.notificationApiService.getByChapter(this.chapterId).subscribe({
            next: (data) => this.chapterNotifications = data,
            error: (err) => {
                console.error('Failed to load notifications', err);
                this.toastr.error('Failed to load notifications');
            }
        });
    }

    createNotification(): void {
        if (this.notificationForm.invalid) {
            this.notificationForm.markAllAsTouched();
            this.toastr.error('Please fill in required fields.');
            return;
        }

        const expireDate = new Date(this.notificationForm.get('expireAt')?.value);
        const expireAtEpoch = Math.floor(expireDate.getTime() / 1000);

        const notification: any = {
            chapterId: this.chapterId,
            message: this.notificationForm.get('message')?.value,
            targetURL: this.notificationForm.get('targetURL')?.value,
            expireAt: expireAtEpoch,
            addedBy: sessionStorage.getItem('username') || 'admin'
        };

        if (this.editingNotification) {
            notification.notificationId = this.editingNotification.notificationId;
            this.notificationApiService.update(notification).subscribe({
                next: () => {
                    this.toastr.success('Notification updated successfully!');
                    this.resetNotificationForm();
                    this.loadChapterNotifications();
                },
                error: (err) => {
                    console.error('Failed to update notification', err);
                    this.toastr.error('Failed to update notification');
                }
            });
        } else {
            this.notificationApiService.create(notification).subscribe({
                next: () => {
                    this.toastr.success('Notification created successfully!');
                    this.resetNotificationForm();
                    this.loadChapterNotifications();
                },
                error: (err) => {
                    console.error('Failed to create notification', err);
                    this.toastr.error('Failed to create notification');
                }
            });
        }
    }

    editNotification(notification: ChapterNotification): void {
        this.editingNotification = notification;
        const expireDate = new Date(notification.expireAt * 1000);
        const dateStr = expireDate.toISOString().substring(0, 10);
        this.notificationForm.patchValue({
            message: notification.message,
            targetURL: notification.targetURL,
            expireAt: dateStr
        });
    }

    resetNotificationForm(): void {
        this.editingNotification = null;
        this.notificationForm.reset();
    }

    toggleNotificationActive(notification: ChapterNotification): void {
        this.notificationApiService.toggleActive(
            notification.chapterId, notification.notificationId, !notification.active
        ).subscribe({
            next: () => {
                this.toastr.success(`Notification ${!notification.active ? 'activated' : 'deactivated'}`);
                this.loadChapterNotifications();
            },
            error: (err) => {
                console.error('Failed to toggle notification', err);
                this.toastr.error('Failed to toggle notification');
            }
        });
    }

    deleteNotification(notification: ChapterNotification): void {
        if (!confirm('Are you sure you want to delete this notification?')) return;
        this.notificationApiService.delete(notification.chapterId, notification.notificationId).subscribe({
            next: () => {
                this.toastr.success('Notification deleted');
                this.loadChapterNotifications();
            },
            error: (err) => {
                console.error('Failed to delete notification', err);
                this.toastr.error('Failed to delete notification');
            }
        });
    }

    formatEpoch(epoch: number): string {
        return new Date(epoch * 1000).toLocaleDateString();
    }

    updateWelcomeNote(): void {
        if (this.chapterForm.valid) {
            this.spinner.show(); // ✅ Show spinner before API call
            const chapterData = this.chapterForm.value;
            this.chapterApiService.updateWelcomeNote(this.chapterId, chapterData).pipe(
                finalize(() => this.spinner.hide()) // ✅ Hide spinner after outermost call completes
            ).subscribe(
                (response) => {
                    console.log('Notes updated successfully', response);
                    this.chapterCreationError = false;
                    // Navigate to chapter list or another appropriate page
                    this.showWelcomeNote = true;
                    this.toastr.success('Notes updated successfully!', 'Success');
                },
                (error) => {
                    console.error('Failed to create chapter', error);
                    this.chapterCreationError = true;
                    this.toastr.error('Notes failed to create.', 'Submission Failed');
                }
            );
        } else {
            this.toastr.error('Please fix the errors in the form.', 'Submission Failed');
        }
    }


}



